import React, { useEffect, useState } from "react";

import MobileOtpStep1 from "./ApplyNowSteps/MobileOtpStep1";
import ApplyLoanDigitapIdentityStep1 from "./ApplyNowSteps/ApplyLoanDigitapIdentityStep1";
import LandingLoanForm from "./ApplyNowSteps/LandingLoanForm";
import ThankYou1 from "./ApplyNowSteps/ThankYou1";
import ApplyLoanSidebar from "./ApplyNowSteps/ApplyLoanSidebar";
import {
  clearLoanStepDraft,
  clearStep2ContactRestore,
} from "../utils/applyFlowStorage";
import {
  markApplyLoanActive,
  resolveApplyLoanResumeStep,
  restoreApplyLoanSecureFields,
  saveApplyLoanDigitapCallbackTxn,
  saveApplyLoanDigitapDraft,
  getApplyLoanDigitapDraft,
  saveApplyLoanStep,
  getDigilockerReturnPath,
  clearDigilockerReturnPath,
} from "../utils/applyLoanStorage";
import { seedApplyLoanProfileFromAadhaar } from "../utils/seedApplyLoanProfile";
import {
  bindEnquiryUnloadAutosave,
} from "../utils/saveEnquiry";
import {
  clearApplyLoanDigitapReturnParams,
  handoffDigitapCallbackToOpenerIfNeeded,
  readApplyLoanDigitapReturnParams,
} from "../utils/digitapKycUtils";

import "../pages/ApplyNow.css";
import "../pages/ApplyNow1.css";

/**
 * /apply-loan — DigiLocker Aadhaar (sendAadhaarOtp2.php).
 * Step 3: lite loan form (no documents) — Full Name from DigiLocker/Aadhaar + income fields.
 * Source = website. /apply-now renders this with same-tab DigiLocker + no location prefill.
 */
const ApplyLoan = ({ sameTabDigilocker = false, prefillLocation = true }) => {
  const [step, setStep] = useState(() => resolveApplyLoanResumeStep());
  const [ready, setReady] = useState(false);
  const [popupHandoff, setPopupHandoff] = useState(false);
  const [digilockerReturn, setDigilockerReturn] = useState(null);

  useEffect(() => {
    // Same-tab DigiLocker started on another page (e.g. /apply-now) → send callback back there
    const earlyCb = readApplyLoanDigitapReturnParams();
    const returnPath = getDigilockerReturnPath();
    if (earlyCb.txnId || earlyCb.aadhaarVerified) {
      clearDigilockerReturnPath();
      if (returnPath && returnPath !== window.location.pathname) {
        window.location.replace(`${returnPath}${window.location.search}`);
        return;
      }
    }

    // DigiLocker redirected into this popup → return to original form tab
    if (!sameTabDigilocker && handoffDigitapCallbackToOpenerIfNeeded()) {
      setPopupHandoff(true);
      return;
    }

    markApplyLoanActive();
    restoreApplyLoanSecureFields();

    // Keep utm_source across DigiLocker redirects / step resumes
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("utm_source");
      if (fromUrl && String(fromUrl).trim()) {
        localStorage.setItem("utm_source", String(fromUrl).trim());
      }
    } catch {
      /* ignore */
    }

    // DigiLocker callback may land on this tab — capture txn BEFORE scrubbing
    const cb = readApplyLoanDigitapReturnParams();
    if (cb.txnId) {
      saveApplyLoanDigitapCallbackTxn(cb.txnId);
      const existing = getApplyLoanDigitapDraft();
      saveApplyLoanDigitapDraft({
        pan: existing?.pan || "",
        transactionId: cb.txnId,
        kycUrl: existing?.kycUrl || "",
        startedAt: existing?.startedAt || Date.now(),
        mobileVerified: true,
        panVerified: true,
      });
      setDigilockerReturn(cb);
      clearApplyLoanDigitapReturnParams();
    }

    let resume = resolveApplyLoanResumeStep();
    // If DigiLocker pending/callback and identity not done yet → stay on step 2
    const pending = getApplyLoanDigitapDraft();
    if (
      resume < 3 &&
      (cb.txnId || pending?.transactionId) &&
      localStorage.getItem("mobile_verified") === "true"
    ) {
      resume = 2;
    }
    if (resume === 3) {
      seedApplyLoanProfileFromAadhaar();
    }
    setStep(resume);
    saveApplyLoanStep(resume);
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => bindEnquiryUnloadAutosave(), []);

  const goToStep = (next) => {
    const n = Number(next);
    setStep(n);
    saveApplyLoanStep(n);
  };

  const handleEnterAadhaarFromMobile = () => {
    // saveEnquiry already flushed in MobileOtpStep1.verifyOtp — do not double-hit
    clearStep2ContactRestore();
    clearLoanStepDraft();
    goToStep(2);
  };

  const handleEnterLoanFromIdentity = () => {
    // saveEnquiry already flushed in DigiLocker finalize — do not double-hit
    clearStep2ContactRestore();
    seedApplyLoanProfileFromAadhaar();
    clearLoanStepDraft();
    goToStep(3);
  };

  const handleBackToIdentity = () => {
    goToStep(2);
  };

  const handleSubmitSuccess = () => {
    goToStep(4);
  };

  if (popupHandoff) {
    return (
      <div className="apply-now1-page apply-loan-page" style={{ padding: "3rem 1rem", textAlign: "center" }}>
        <p>Aadhaar verified. Returning to your application…</p>
        <p style={{ fontSize: "0.85rem", color: "#64748b", marginTop: 8 }}>
          If this tab stays open, close it and switch back to your original form tab.
        </p>
      </div>
    );
  }

  if (!ready) {
    return null;
  }

  if (step === 4) {
    return (
      <div className="apply-now1-page apply-now1-page--success apply-loan-page">
        <div className="apply-now1-success-card">
          <ThankYou1 />
        </div>
      </div>
    );
  }

  return (
    <div className="apply-now1-page apply-loan-page">
      <div className="apply-now1-container">
        <header className="apply-now1-header">
          <div>
            <span className="apply-now1-header__eyebrow">Digital Loan Journey</span>
            <h1>Apply Loan</h1>
            <p>Fast, paper-light and secure application flow.</p>
          </div>
          <div className="apply-now1-header__meta">
            <span>Paperless</span>
            <span>DigiLocker</span>
            <span>Quick apply</span>
          </div>
        </header>

        <div className="apply-now1-content">
          <aside className="apply-now1-rail">
            <ApplyLoanSidebar currentStep={step} />
          </aside>

          <main className="apply-now1-workspace">
            {step === 1 && (
              <div className="apply-now1-stage apply-now1-stage--focus apply-loan-stage--mobile">
                <MobileOtpStep1
                  onNext={handleEnterAadhaarFromMobile}
                  enquirySource="website"
                />
              </div>
            )}

            {step === 2 && (
              <div className="apply-now1-stage apply-now1-stage--focus apply-loan-stage--mobile">
                <ApplyLoanDigitapIdentityStep1
                  onNext={handleEnterLoanFromIdentity}
                  enquirySource="website"
                  sameTab={sameTabDigilocker}
                  digilockerReturn={digilockerReturn}
                />
              </div>
            )}

            {step === 3 && (
              <div className="apply-now1-stage-card apply-now1-stage-card--scroll apply-loan-lite-form">
                <LandingLoanForm
                  leadSource="website"
                  leadSaveMode="taskApi1"
                  prefillLocation={prefillLocation}
                  onBack={handleBackToIdentity}
                  onNext={handleSubmitSuccess}
                />
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ApplyLoan;
